import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table"
import { DocumentDriverContext, Message } from "@/reducers/messagesSlice"
import { driverJid } from "@/jidUtils"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import { useCallback, useContext, useMemo, useState } from "react"
import {
  hideImageGallery,
  hidePDFViewer,
  updateImageGallery,
  updatePDFViewer,
} from "@/reducers/overlaysSlice"
import { ArrowLeft, Link, Unlink2 } from "lucide-react"
import { Location } from "@/reducers/locationsSlice"
import { isMobile } from "@/utils/detectDevice"
import { mapFileUrlToSameOrigin } from "@/components/message/utils/mapFileToSameOrigin"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  LinkDeliveryOrderDialog,
  LinkDocumentInfo,
} from "./LinkDeliveryOrderDialog"
import { createPortal } from "react-dom"
import AdHocCommands from "@/api/adHocCommands"
import { XmppContext } from "@/stream/xmppClient"
import { toast } from "sonner"
import { LicensePlate } from "@/components/ui/license-plate"
import { selectActiveChat } from "@/reducers/chatsSliceSelectors"
import { messageAuthorName } from "@/reducers/profilesSliceSelectors"
import { isManualDocumentLinkingEnabled } from "@/lib/gpsDocumentLinks"
import { ArchivedChatContext } from "@/contexts/ArchivedChatContext"
import { useTranslation } from "react-i18next"
import type enReports from "@/lib/i18n/locales/en/reports.json"

// Labels for the metadata keys the wizard extracts. The keys stay as the
// server sends them; the label is translated at render.
type FieldLabelKey =
  `reports.cmrMetaData.fields.${keyof typeof enReports.cmrMetaData.fields}`

const fieldLabelKeys: Record<string, FieldLabelKey> = {
  // Top-level CMR fields
  CMRNumber: "reports.cmrMetaData.fields.cmrNumber",
  AttachedDocuments: "reports.cmrMetaData.fields.attachedDocuments",
  CarrierPlates: "reports.cmrMetaData.fields.carrierPlates",
  GoodsDescription: "reports.cmrMetaData.fields.goodsDescription",
  WeightGross: "reports.cmrMetaData.fields.weightGross",

  // Top-level Lieferschein fields
  LieferscheinNumber: "reports.cmrMetaData.fields.lieferscheinNumber",
  CustomerNumber: "reports.cmrMetaData.fields.customerNumber",
  CustomerReference: "reports.cmrMetaData.fields.customerReference",
  TransportNumber: "reports.cmrMetaData.fields.transportNumber",
  DeliveryTerms: "reports.cmrMetaData.fields.deliveryTerms",
  Remarks: "reports.cmrMetaData.fields.remarks",
  TotalGrossWeight: "reports.cmrMetaData.fields.totalGrossWeight",
  TotalNetWeight: "reports.cmrMetaData.fields.totalNetWeight",
  TotalPackages: "reports.cmrMetaData.fields.totalPackages",

  // Carrier and its children
  Carrier: "reports.cmrMetaData.fields.carrier",
  "Carrier.Address": "reports.cmrMetaData.fields.carrierAddress",
  "Carrier.Name": "reports.cmrMetaData.fields.carrierName",
  "Carrier.Signature": "reports.cmrMetaData.fields.carrierSignature",

  // Delivery and its children
  Delivery: "reports.cmrMetaData.fields.delivery",
  "Delivery.Address": "reports.cmrMetaData.fields.deliveryAddress",
  "Delivery.Date": "reports.cmrMetaData.fields.deliveryDate",

  // GoodItem and its children (CMR)
  GoodItem: "reports.cmrMetaData.fields.goodItem",
  "GoodItem.Description": "reports.cmrMetaData.fields.goodItemDescription",
  "GoodItem.Id": "reports.cmrMetaData.fields.goodItemId",
  "GoodItem.NumberOfPackages":
    "reports.cmrMetaData.fields.goodItemNumberOfPackages",
  "GoodItem.Volume": "reports.cmrMetaData.fields.goodItemVolume",
  "GoodItem.Weight": "reports.cmrMetaData.fields.goodItemWeight",

  // LineItem and its children (Lieferschein)
  LineItem: "reports.cmrMetaData.fields.lineItem",
  "LineItem.Position": "reports.cmrMetaData.fields.lineItemPosition",
  "LineItem.ArticleNumber": "reports.cmrMetaData.fields.lineItemArticleNumber",
  "LineItem.Description": "reports.cmrMetaData.fields.lineItemDescription",
  "LineItem.Quantity": "reports.cmrMetaData.fields.lineItemQuantity",
  "LineItem.Unit": "reports.cmrMetaData.fields.lineItemUnit",
  "LineItem.GrossWeight": "reports.cmrMetaData.fields.lineItemGrossWeight",
  "LineItem.NetWeight": "reports.cmrMetaData.fields.lineItemNetWeight",
  "LineItem.Volume": "reports.cmrMetaData.fields.lineItemVolume",

  // Pickup and its children
  Pickup: "reports.cmrMetaData.fields.pickup",
  "Pickup.Address": "reports.cmrMetaData.fields.pickupAddress",
  "Pickup.Date": "reports.cmrMetaData.fields.pickupDate",

  // Receiver and its children
  Receiver: "reports.cmrMetaData.fields.receiver",
  "Receiver.Address": "reports.cmrMetaData.fields.receiverAddress",
  "Receiver.Name": "reports.cmrMetaData.fields.receiverName",
  "Receiver.Signature": "reports.cmrMetaData.fields.receiverSignature",

  // Sender and its children
  Sender: "reports.cmrMetaData.fields.sender",
  "Sender.Address": "reports.cmrMetaData.fields.senderAddress",
  "Sender.Name": "reports.cmrMetaData.fields.senderName",
  "Sender.Signature": "reports.cmrMetaData.fields.senderSignature",

  // Receipt fields (wizard's extract_receipt)
  "Receipt.Kind": "reports.cmrMetaData.fields.receiptKind",
  "Receipt.Category": "reports.cmrMetaData.fields.receiptCategory",
  "Receipt.Date": "reports.cmrMetaData.fields.receiptDate",
  "Receipt.Time": "reports.cmrMetaData.fields.receiptTime",
  "Receipt.Number": "reports.cmrMetaData.fields.receiptNumber",
  Vendor: "reports.cmrMetaData.fields.vendor",
  "Vendor.Name": "reports.cmrMetaData.fields.vendorName",
  "Vendor.Address": "reports.cmrMetaData.fields.vendorAddress",
  "Vendor.TaxId": "reports.cmrMetaData.fields.vendorTaxId",
  "Vendor.Country": "reports.cmrMetaData.fields.vendorCountry",
  Amount: "reports.cmrMetaData.fields.amount",
  "Amount.Gross": "reports.cmrMetaData.fields.amountGross",
  "Amount.Net": "reports.cmrMetaData.fields.amountNet",
  "Amount.Vat": "reports.cmrMetaData.fields.amountVat",
  "Amount.VatRate": "reports.cmrMetaData.fields.amountVatRate",
  "Amount.Currency": "reports.cmrMetaData.fields.amountCurrency",
  "Payment.Method": "reports.cmrMetaData.fields.paymentMethod",
  "Vehicle.Plates": "reports.cmrMetaData.fields.vehiclePlates",
  "Fuel.Liters": "reports.cmrMetaData.fields.fuelLiters",
  Items: "reports.cmrMetaData.fields.items",
}

// "car_wash" -> "car wash"; the values are the wizard's enum keys.
const humanizeEnum = (value: string) => value.replace(/_/g, " ")

const formatReceiptAmount = (metadata: Record<string, string>) =>
  [metadata["Amount.Gross"], metadata["Amount.Currency"]]
    .filter(Boolean)
    .join(" ")

const formatReceiptDate = (metadata: Record<string, string>) =>
  [metadata["Receipt.Date"], metadata["Receipt.Time"]].filter(Boolean).join(" ")

const getCoordinates = (location: Location) => {
  if (location.lat && location.lon) {
    const lat = parseFloat(location.lat).toFixed(5)
    const lon = parseFloat(location.lon).toFixed(5)
    return `${lat}, ${lon}`
  }
  return ""
}

const formatAddress = (location: Location) => {
  const coordinates = getCoordinates(location)
  if ((location.countryCode || location.country) && location.locality) {
    return `${location.countryCode ?? location.country}, ${location.postalCode ? location.postalCode : ""} ${location.locality}`
  }

  if (coordinates) {
    return coordinates
  }

  if (location.formattedAddress) {
    return location.formattedAddress
  }

  return "?"
}

function SignaturePill({ signed }: { signed?: boolean }) {
  const { t } = useTranslation()
  if (signed) {
    return (
      <span className="inline-block rounded-md bg-green-200 px-1.5 py-0.5 text-xs font-medium text-green-700">
        {t("reports.cmrMetaData.signed")}
      </span>
    )
  }
  return (
    <span className="inline-block rounded-md bg-red-200 px-1.5 py-0.5 text-xs font-medium text-red-700">
      {t("reports.cmrMetaData.notSigned")}
    </span>
  )
}

export default function CMRMetaData({
  metadata = {},
  driverContext,
  message,
  showAllData,
  inSearch = false,
  className,
  documentType,
  blurry,
  remarks,
  linkInfo,
  onLinked,
  showMoreButton = true,
}: {
  metadata: Record<string, string> | undefined
  driverContext?: DocumentDriverContext | undefined
  message: Message | undefined
  showAllData?: boolean
  showMoreButton?: boolean
  inSearch?: boolean
  className?: string
  documentType?: string
  blurry?: boolean
  remarks?: string
  linkInfo?: LinkDocumentInfo & {
    driverJid: string
    unlinkInfo?: {
      deliveryOrderMessageId: string
      messageRoomJid: string
    }
  }
  onLinked?: () => void
}) {
  const { t } = useTranslation()
  const fieldLabel = (key: string): string => {
    const labelKey = fieldLabelKeys[key]
    return labelKey ? t(labelKey) : key
  }
  const dispatch = useAppDispatch()
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const activeChat = useAppSelector(selectActiveChat)
  const archive = useContext(ArchivedChatContext)
  const messageChatJid = archive?.chatJid ?? activeChat?.jid

  const handleShowMoreInfo = useCallback(() => {
    if (message?.url && !/\.pdf$/i.test(message.url)) {
      dispatch(
        updateImageGallery({
          visible: true,
          initialImage: mapFileUrlToSameOrigin(message.url),
          drawerOpen: true,
        }),
      )
    } else {
      dispatch(
        updatePDFViewer({
          visible: true,
          PDFMessage: message,
          drawerOpen: true,
        }),
      )
    }
  }, [dispatch, message])
  let driverBareJid = ""
  try {
    driverBareJid = message?.from ? driverJid(message.from) : ""
  } catch {
    // from may not be a valid MUC occupant JID
  }
  const { myJid } = useContext(XmppContext)
  const manualLinking = isManualDocumentLinkingEnabled(myJid?.getDomain())

  const authorName = useAppSelector(messageAuthorName(message, myJid))

  // Derive linkInfo from message if not explicitly provided
  const effectiveLinkInfo = useMemo(
    () =>
      linkInfo ??
      (message?.id && message?.url && message?.to && driverBareJid
        ? {
            cmrMessageId: message.id,
            cmrFileUrl: message.url,
            cmrFileName: message.url.split("/").pop() || "",
            chatJid: messageChatJid,
            driverJid: driverBareJid,
          }
        : undefined),
    [
      messageChatJid,
      driverBareJid,
      linkInfo,
      message?.id,
      message?.to,
      message?.url,
    ],
  )

  // Check if this document is already linked to a delivery order
  // First check linkInfo.unlinkInfo (passed directly from CMR report), then fall back to Redux
  const chatJid = effectiveLinkInfo?.chatJid
  const cmrMessageId = effectiveLinkInfo?.cmrMessageId

  const reduxLink = useAppSelector((state) => {
    if (!chatJid || !cmrMessageId) return undefined
    return state.messages[chatJid]?.documentLinksByCmrMessageId?.[cmrMessageId]
  })
  const archivedLink =
    archive && archive.chatJid === chatJid && cmrMessageId
      ? archive.chatMessages.documentLinksByCmrMessageId?.[cmrMessageId]
      : undefined
  const storedLink = reduxLink ?? archivedLink
  const isAlreadyLinked = !!effectiveLinkInfo?.unlinkInfo || !!storedLink
  // GPS links are read-only here: unlinking goes through wizard stanzas only.
  const canUnlink =
    !!effectiveLinkInfo?.unlinkInfo || !!storedLink?.deliveryOrderMessageId

  const { client } = useContext(XmppContext)
  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false)
  const [unlinking, setUnlinking] = useState(false)

  const handleUnlink = useCallback(async () => {
    if (!effectiveLinkInfo || !client) return
    const roomJid =
      effectiveLinkInfo.unlinkInfo?.messageRoomJid ||
      storedLink?.to ||
      effectiveLinkInfo.chatJid
    if (!roomJid) return
    setUnlinking(true)
    try {
      await AdHocCommands.linkDocument({
        client,
        cmrMessageId: effectiveLinkInfo.cmrMessageId,
        deliveryOrderMessageId: "",
        cmrFileUrl: effectiveLinkInfo.cmrFileUrl,
        cmrFileName: effectiveLinkInfo.cmrFileName,
        deliveryOrderFileUrl: "",
        deliveryOrderFileName: "",
        messageRoomJid: roomJid,
        triggeringRoomJid: roomJid,
      })
      dispatch(hideImageGallery())
      dispatch(hidePDFViewer())
      onLinked?.()
    } catch (error) {
      console.error(error)
      toast.error(t("reports.cmrMetaData.unlinkFailed"))
    } finally {
      setUnlinking(false)
      setUnlinkDialogOpen(false)
    }
  }, [effectiveLinkInfo, storedLink, client, onLinked, dispatch, t])

  const handleClose = useCallback(() => {
    dispatch(hideImageGallery())
    dispatch(hidePDFViewer())
  }, [dispatch])

  if (!metadata && !driverContext) return null

  const isCMR = documentType?.toLowerCase() === "cmr"
  const senderSigned = Boolean(metadata?.["Sender.Signature"])
  const receiverSigned = Boolean(metadata?.["Receiver.Signature"])
  const carrierSigned = Boolean(metadata?.["Carrier.Signature"])

  const plates = driverContext?.plates
  const location = driverContext?.location
  const odometer = driverContext?.location?.odometer

  // Signature Status Section (CMR only)
  const signatureSection = isCMR && (
    <Card className="px-0 py-0 shadow-none">
      <CardContent className="px-0 pb-0">
        <Table className="text-left text-xs">
          <TableBody>
            <TableRow className="h-9">
              <TableCell className="w-[106px] truncate py-2 text-xs">
                {fieldLabel("Sender")}
              </TableCell>
              <TableCell className="py-2">
                <SignaturePill signed={senderSigned} />
              </TableCell>
            </TableRow>
            <TableRow className="h-9">
              <TableCell className="w-[106px] truncate py-2 text-xs">
                {fieldLabel("Receiver")}
              </TableCell>
              <TableCell className="py-2">
                <SignaturePill signed={receiverSigned} />
              </TableCell>
            </TableRow>
            <TableRow className="h-9">
              <TableCell className="w-[106px] truncate py-2 text-xs">
                {fieldLabel("Carrier")}
              </TableCell>
              <TableCell className="py-2">
                <SignaturePill signed={carrierSigned} />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )

  // Remarks Section
  const remarksSection = remarks?.trim() && (
    <Card className="my-1 px-0 py-0 shadow-none">
      <CardContent className="flex flex-col gap-1 px-4 py-3">
        <p className="text-xs font-semibold text-destructive">
          {fieldLabel("Remarks")}
        </p>
        <p className="text-xs text-destructive">{remarks}</p>
      </CardContent>
    </Card>
  )

  // Blurry Warning Section
  const blurrySection = blurry && (
    <Card className="my-1 px-0 py-0 shadow-none">
      <CardContent className="flex flex-col gap-1 px-2 py-2">
        <p className="text-xs font-semibold text-destructive">
          {t("reports.cmrMetaData.blurry")}
        </p>
      </CardContent>
    </Card>
  )

  // Compact info rows
  const compactInfoRows = (
    <>
      {plates && (
        <TableRow key="plates" className="h-[38px]">
          <TableCell className="w-[106px] truncate py-2 text-xs">
            {t("reports.cmrMetaData.driverPlates")}
          </TableCell>
          <TableCell className="py-2 text-xs font-medium">
            <LicensePlate plateNumber={plates} />
          </TableCell>
        </TableRow>
      )}
      {location && (
        <TableRow key="sent" className="h-[38px]">
          <TableCell className="w-[106px] truncate py-2 text-xs">
            {t("reports.cmrMetaData.sentFrom")}
          </TableCell>
          <TableCell className="py-2 text-xs font-medium">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>{formatAddress(location)}</span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {getCoordinates(location) ||
                      t("reports.cmrMetaData.noCoordinates")}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </TableCell>
        </TableRow>
      )}
      {odometer && (
        <TableRow key="odometer" className="h-[38px]">
          <TableCell className="w-[106px] truncate py-2 text-xs">
            {t("reports.cmrMetaData.odometer")}
          </TableCell>
          <TableCell className="py-2 text-xs font-medium">
            {odometer} km
          </TableCell>
        </TableRow>
      )}
      {metadata?.["Pickup.Address"] && (
        <TableRow key="pickupAddress" className="h-[38px]">
          <TableCell className="w-[106px] truncate py-2 text-xs">
            {fieldLabel("Pickup.Address")}
          </TableCell>
          <TableCell className="py-2 text-xs font-medium">
            {metadata["Pickup.Address"]}
          </TableCell>
        </TableRow>
      )}
      {metadata?.["Delivery.Address"] && (
        <TableRow key="deliveryAddress" className="h-[38px]">
          <TableCell className="w-[106px] truncate py-2 text-xs">
            {fieldLabel("Delivery.Address")}
          </TableCell>
          <TableCell className="py-2 text-xs font-medium">
            {metadata["Delivery.Address"]}
          </TableCell>
        </TableRow>
      )}
      {(metadata?.["Receiver.Name"] || metadata?.["Receiver.Address"]) && (
        <TableRow key="receiver" className="h-[38px]">
          <TableCell className="w-[106px] truncate py-2 text-xs">
            {fieldLabel("Receiver")}
          </TableCell>
          <TableCell className="py-2 text-xs font-medium">
            {metadata["Receiver.Name"]}
            {metadata["Receiver.Name"] && metadata["Receiver.Address"] && (
              <br />
            )}
            {metadata["Receiver.Address"]}
          </TableCell>
        </TableRow>
      )}
      {(metadata?.["Sender.Name"] || metadata?.["Sender.Address"]) && (
        <TableRow key="sender" className="h-[38px]">
          <TableCell className="w-[106px] truncate py-2 text-xs">
            {fieldLabel("Sender")}
          </TableCell>
          <TableCell className="py-2 text-xs font-medium">
            {metadata["Sender.Name"]}
            {metadata["Sender.Name"] && metadata["Sender.Address"] && <br />}
            {metadata["Sender.Address"]}
          </TableCell>
        </TableRow>
      )}
      {metadata?.["Amount.Gross"] && (
        <TableRow key="amount" className="h-[38px]">
          <TableCell className="w-[106px] truncate py-2 text-xs">
            {fieldLabel("Amount")}
          </TableCell>
          <TableCell className="py-2 text-xs font-medium">
            {formatReceiptAmount(metadata)}
          </TableCell>
        </TableRow>
      )}
      {metadata?.["Receipt.Date"] && (
        <TableRow key="receiptDate" className="h-[38px]">
          <TableCell className="w-[106px] truncate py-2 text-xs">
            {fieldLabel("Receipt.Date")}
          </TableCell>
          <TableCell className="py-2 text-xs font-medium">
            {formatReceiptDate(metadata)}
          </TableCell>
        </TableRow>
      )}
      {(metadata?.["Vendor.Name"] || metadata?.["Vendor.Address"]) && (
        <TableRow key="vendor" className="h-[38px]">
          <TableCell className="w-[106px] truncate py-2 text-xs">
            {fieldLabel("Vendor")}
          </TableCell>
          <TableCell className="py-2 text-xs font-medium">
            {metadata["Vendor.Name"]}
            {metadata["Vendor.Name"] && metadata["Vendor.Address"] && <br />}
            {metadata["Vendor.Address"]}
          </TableCell>
        </TableRow>
      )}
      {metadata?.["Receipt.Category"] && (
        <TableRow key="receiptCategory" className="h-[38px]">
          <TableCell className="w-[106px] truncate py-2 text-xs">
            {fieldLabel("Receipt.Category")}
          </TableCell>
          <TableCell className="py-2 text-xs font-medium capitalize">
            {humanizeEnum(metadata["Receipt.Category"])}
          </TableCell>
        </TableRow>
      )}
    </>
  )

  return (
    <div
      className={`${className} ${isMobile ? "" : "mb-4"} ${!showAllData ? "max-w-[350px]" : ""}`}
    >
      {showAllData && !inSearch && (
        <div className="flex w-full flex-col pb-2">
          {!isMobile && (
            <div className="flex items-center justify-between pb-4">
              <Button
                variant="ghost"
                onClick={handleClose}
                className="self-start pl-0 text-sm"
              >
                <ArrowLeft className="h-4 w-4" />
                {t("common.back")}
              </Button>
              {effectiveLinkInfo && !isAlreadyLinked && manualLinking && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setLinkDialogOpen(true)}
                >
                  <Link className="mr-1 h-3 w-3" />
                  {t("reports.cmrMetaData.link")}
                </Button>
              )}
              {effectiveLinkInfo && canUnlink && manualLinking && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setUnlinkDialogOpen(true)}
                >
                  <Unlink2 className="mr-1 h-3 w-3" />
                  {t("reports.cmrMetaData.unlink")}
                </Button>
              )}
            </div>
          )}
          <h1 className="text-xl font-bold">{authorName}</h1>
        </div>
      )}

      {/* Document Type */}
      {documentType && (
        <Card className="my-1 px-0 py-0 shadow-none">
          <CardContent className="px-0 pb-0">
            <Table className="text-left text-xs">
              <TableBody>
                <TableRow className="h-[38px]">
                  <TableCell className="w-[106px] truncate py-2 text-xs">
                    {t("reports.cmrMetaData.documentType")}
                  </TableCell>
                  <TableCell className="py-2 text-xs font-medium capitalize">
                    {documentType}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Signature Status */}
      {signatureSection}

      {/* Remarks */}
      {remarksSection}

      {/* Blurry Warning */}
      {blurrySection}

      {/* Info Table */}
      <Card
        className={`${showAllData ? "px-0 py-0 shadow-none" : "shadow-none"} mt-2 px-0 py-0`}
      >
        <CardContent className="px-0 pb-0">
          <Table className="text-left text-xs">
            <TableBody>
              {compactInfoRows}
              {showAllData &&
                Object.entries(metadata).map(([key, value]) => (
                  <TableRow key={key} className="h-[38px]">
                    <TableCell className="w-[106px] truncate py-2 text-xs">
                      {fieldLabel(key)}
                    </TableCell>
                    <TableCell className="py-2 text-xs font-medium">
                      {value}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      {!showAllData && showMoreButton && (
        <div className="my-4 text-start">
          <Button
            onClick={handleShowMoreInfo}
            variant="outline"
            size="sm"
            className={`h-8 text-xs ${isMobile ? "w-full" : ""}`}
          >
            {t("reports.cmrMetaData.showMore")}
          </Button>
        </div>
      )}
      {effectiveLinkInfo && (
        <LinkDeliveryOrderDialog
          open={linkDialogOpen}
          onOpenChange={setLinkDialogOpen}
          driverJid={effectiveLinkInfo.driverJid}
          cmrInfo={effectiveLinkInfo}
          onLinked={onLinked}
        />
      )}
      {unlinkDialogOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/80"
            onClick={() => !unlinking && setUnlinkDialogOpen(false)}
          >
            <div
              className="w-full max-w-lg space-y-4 border bg-background p-6 shadow-lg sm:rounded-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="space-y-2">
                <h2 className="text-lg font-semibold">
                  {t("reports.cmrMetaData.unlinkTitle")}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {t("reports.cmrMetaData.unlinkConfirm")}
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  disabled={unlinking}
                  onClick={() => setUnlinkDialogOpen(false)}
                >
                  {t("common.cancel")}
                </Button>
                <Button disabled={unlinking} onClick={handleUnlink}>
                  {unlinking
                    ? t("reports.cmrMetaData.unlinking")
                    : t("reports.cmrMetaData.unlink")}
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}
